import type { PriceLineResponse } from '../model/types';

/**
 * The base-slot rule (design spec `docs/design/product-card-pricing-spec.md` §8.0, normative) —
 * the single source of truth for "is this line the product's base price". Closes the R0 defect:
 * `ProductEditPage`'s old `activeBasePriceLine` filtered only `!line.channelCode`, so a price-LIST
 * line or a customer-GROUP line (both `channelCode == null` after plan-platform-002) could render
 * as the product's base price on the hero tile and feed the readiness checklist. `priceGrid.ts`'s
 * grid-cell resolution and both backends already apply the correct three-discriminator test; this
 * module is that same test, exported once for every card/editor consumer.
 *
 * WP0.1 ships only the BASE-slot subset of the design's eventual `priceSlots.ts` (which also names
 * generic, any-slot helpers — `slotOf`, `slotKey`, `groupBySlot`, `summarizeBase`, `negativeLine`
 * — for the Phase-1 "other prices of this product" section (spec §8.5) and the tile badge).
 * Those land when Phase 1 (WP1.2/WP1.3) needs them; adding them speculatively here would be
 * scope creep for a fix that must "ship independently, first".
 */

const TERMINAL_STATES = new Set(['ACTIVE', 'EXPIRED']);

/**
 * **Base slot** — a price line whose `channelCode`, `customerGroupCode` **and** `priceListCode`
 * are all null/absent. A line with *any* of the three set belongs to another slot, whatever its
 * state. `!channelCode` alone is never a base test (the R0 defect).
 */
export function isBaseSlot(
  line: Pick<PriceLineResponse, 'channelCode' | 'customerGroupCode' | 'priceListCode'>,
): boolean {
  return !line.channelCode && !line.customerGroupCode && !line.priceListCode;
}

/** True when `line` is ACTIVE and its validity window covers `now` (half-open `[validFrom, validTo)`). */
function coversNow(line: PriceLineResponse, now: Date): boolean {
  if (line.state !== 'ACTIVE') return false;
  if (line.validFrom && now < new Date(line.validFrom)) return false;
  if (line.validTo && now >= new Date(line.validTo)) return false;
  return true;
}

/**
 * The base slot's **governing line** at instant `now` (§8.0) — the base-slot line whose state is
 * `ACTIVE` and whose validity window covers `now`. The engine's exclusion constraint guarantees at
 * most one; if the read model ever returns several (replay/drift), the tie-break is the **latest
 * `validFrom`**, then the latest `createdAt` — **never `createdAt` alone**, so a future-dated line
 * with a newer `createdAt` can never outrank the line actually in force today.
 */
export function governingBaseLine(lines: PriceLineResponse[], now: Date): PriceLineResponse | undefined {
  return lines
    .filter((line) => isBaseSlot(line) && coversNow(line, now))
    .sort((a, b) => {
      const byValidFrom = (b.validFrom ?? '').localeCompare(a.validFrom ?? '');
      if (byValidFrom !== 0) return byValidFrom;
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    })[0];
}

/**
 * The base slot's **scheduled line** (§8.0/§8.1/§8.3) — "the next change": the base-slot line that
 * is `ACTIVE` but whose window has not started yet, earliest `validFrom` first.
 */
export function scheduledBaseLine(lines: PriceLineResponse[], now: Date): PriceLineResponse | undefined {
  return lines
    .filter(
      (line) => isBaseSlot(line) && line.state === 'ACTIVE' && Boolean(line.validFrom) && new Date(line.validFrom!) > now,
    )
    .sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''))[0];
}

/**
 * The newest non-ACTIVE base-slot submission newer (`createdAt`) than `governing` — or, when there
 * is no governing line, the newest non-ACTIVE base-slot submission at all (§8.0/§8.1: pending —
 * DRAFT/PENDING_VALIDATION/REQUIRES_APPROVAL — and negative — BLOCKED/REJECTED — submissions share
 * this one lookup; Phase 1's tile badge and drawer rows tell them apart by `state`). `EXPIRED`
 * never qualifies — it "never displays outside the History section" per §8.0.
 */
export function pendingBaseSubmission(
  lines: PriceLineResponse[],
  governing: PriceLineResponse | undefined,
): PriceLineResponse | undefined {
  return lines
    .filter(
      (line) =>
        isBaseSlot(line) &&
        !TERMINAL_STATES.has(line.state) &&
        (!governing || (line.createdAt ?? '').localeCompare(governing.createdAt ?? '') > 0),
    )
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
}

/**
 * The unit amount of a line: the tier whose `minQuantity === 1`, else the lowest-quantity tier.
 * Shared so callers (the tile, the editor, the grid) stop re-implementing the same tier lookup.
 */
export function unitAmountOf(line: PriceLineResponse | undefined): number | undefined {
  const tier =
    line?.tiers.find((t) => t.minQuantity === 1) ??
    [...(line?.tiers ?? [])].sort((a, b) => a.minQuantity - b.minQuantity)[0];
  return tier?.amount;
}

// ── Slot-generic derivation (Phase 1, plan-merchant-portal-018 WP1.1/WP1.2 — the rest of spec
// §8.0's API) — §8.5's "other prices" section needs the SAME governing/scheduled/pending/negative
// derivation for a channel, a channel+group, a group or a price-list slot, not only the base slot
// WP0.1 shipped, and `PriceLineEditorForm`'s own seeding (when opened without an explicit
// `targetLine`) needs a governing-by-validity lookup for every context, not just `type: 'base'`.
// Every function below is a strict generalisation of its base-only counterpart above — same
// tie-break rules, parameterised by an explicit slot descriptor — and none of them touch the
// base-only functions above, which stay the ergonomic, unit-tested entry point `ProductEditPage`'s
// tile/readiness already depend on (adr-merchant-portal-018 reconciliation note 2: the ADR adopts
// this slot-generic surface from the spec; the base-only names WP0.1 shipped are additive, not
// superseded).

const PENDING_STATES = new Set(['DRAFT', 'PENDING_VALIDATION', 'REQUIRES_APPROVAL']);
const NEGATIVE_STATES = new Set(['BLOCKED', 'REJECTED']);

/**
 * A slot's three discriminators, in the SAME `string | undefined` shape `PriceLineResponse` itself
 * uses — so a line can be passed anywhere a `PriceSlot` is expected with no conversion, and
 * `slotOf(line)` is a pure pass-through. All three absent/undefined IS the base slot (§8.0).
 */
export type PriceSlot = Pick<PriceLineResponse, 'channelCode' | 'customerGroupCode' | 'priceListCode'>;

/** The base slot descriptor — every discriminator absent. One canonical instance so callers never
 * re-write `{}` (or, worse, `{ channelCode: undefined, ... }`) at each call site. */
export const BASE_SLOT: PriceSlot = {};

/** A line's own slot descriptor (§8.0). */
export function slotOf(line: PriceSlot): PriceSlot {
  return {
    channelCode: line.channelCode,
    customerGroupCode: line.customerGroupCode,
    priceListCode: line.priceListCode,
  };
}

/**
 * A stable string key for a slot — used to group lines (`groupBySlot`) and to test slot equality.
 * Falsy-normalises every discriminator (`||`, not `??`) so an empty-string discriminator is treated
 * as absent, exactly like `isBaseSlot`'s own truthiness test: the two notions of "absent" must never
 * drift, or the R0 leak class could reappear through this door instead.
 */
export function slotKey(line: PriceSlot): string {
  return `${line.priceListCode || ''} ${line.channelCode || ''} ${line.customerGroupCode || ''}`;
}

function slotMatches(line: PriceLineResponse, slot: PriceSlot): boolean {
  return slotKey(line) === slotKey(slot);
}

/** Latest `validFrom`, then latest `createdAt` as a residual tiebreak — never `createdAt` alone
 * (§8.0) — the same order `governingBaseLine` sorts by, factored out so both share one definition. */
function byValidityDesc(a: PriceLineResponse, b: PriceLineResponse): number {
  const byValidFrom = (b.validFrom ?? '').localeCompare(a.validFrom ?? '');
  if (byValidFrom !== 0) return byValidFrom;
  return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
}

/** The **governing line** of `slot` at `now` (§8.0) — generalises `governingBaseLine` to any slot:
 * channel, channel+group, group or price-list. */
export function governingLine(
  lines: PriceLineResponse[],
  slot: PriceSlot,
  now: Date,
): PriceLineResponse | undefined {
  return lines.filter((line) => slotMatches(line, slot) && coversNow(line, now)).sort(byValidityDesc)[0];
}

/** The **scheduled line** of `slot` ("the next change") — generalises `scheduledBaseLine`. */
export function scheduledLine(
  lines: PriceLineResponse[],
  slot: PriceSlot,
  now: Date,
): PriceLineResponse | undefined {
  return lines
    .filter(
      (line) =>
        slotMatches(line, slot) &&
        line.state === 'ACTIVE' &&
        Boolean(line.validFrom) &&
        new Date(line.validFrom!) > now,
    )
    .sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''))[0];
}

/**
 * The newest **pending** submission of `slot` — state ∈ {DRAFT, PENDING_VALIDATION,
 * REQUIRES_APPROVAL} (§8.0). Unlike `negativeLine`, no "newer than governing" gate: a pending
 * submission is always an open question the merchant must resolve regardless of what currently
 * governs the slot (this mirrors the spec's own `summarizeBase` field list, which carries `pending`
 * and `negative` as two DISTINCT facade fields, not one combined lookup).
 */
export function pendingLine(lines: PriceLineResponse[], slot: PriceSlot): PriceLineResponse | undefined {
  return lines
    .filter((line) => slotMatches(line, slot) && PENDING_STATES.has(line.state))
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
}

/**
 * The newest **negative** (BLOCKED/REJECTED) submission of `slot`, shown only when newer
 * (`createdAt`) than `governing` — or any, when there is no governing line (§8.0): a rejected
 * submission superseded by a later, healthy governing line is moot and must not alarm the merchant.
 */
export function negativeLine(
  lines: PriceLineResponse[],
  slot: PriceSlot,
  governing: PriceLineResponse | undefined,
): PriceLineResponse | undefined {
  return lines
    .filter(
      (line) =>
        slotMatches(line, slot) &&
        NEGATIVE_STATES.has(line.state) &&
        (!governing || (line.createdAt ?? '').localeCompare(governing.createdAt ?? '') > 0),
    )
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
}

/**
 * Groups `lines` by slot (§8.0) — the base slot's own lines are included; callers decide whether to
 * exclude them (§8.5: "`groupBySlot(existingLines)` minus the base slot"). Iteration order is
 * insertion order (a plain JS `Map`) — a caller needing a DISPLAY order (ladder order, then a
 * registry-resolved name) sorts the entries itself once names are available; this module stays
 * presentation-agnostic.
 */
export function groupBySlot(lines: PriceLineResponse[]): Map<string, PriceLineResponse[]> {
  const groups = new Map<string, PriceLineResponse[]>();
  for (const line of lines) {
    const key = slotKey(line);
    const bucket = groups.get(key);
    if (bucket) bucket.push(line);
    else groups.set(key, [line]);
  }
  return groups;
}

export interface BaseSummary {
  governing: PriceLineResponse | undefined;
  scheduled: PriceLineResponse | undefined;
  pending: PriceLineResponse | undefined;
  negative: PriceLineResponse | undefined;
  /** Count of DISTINCT non-base slots with at least one line — the tile's "N more prices" caption
   * (§8.1, WP1.3) and the drawer's "OTHER PRICES (N)" heading (§8.5, WP1.2) share this exact number. */
  otherSlots: number;
}

/**
 * The base-slot facade (§8.0) — computes all four base-slot signals plus the other-slots count from
 * ONE call, instead of re-deriving each with the base-only functions above one at a time. Backs
 * `ProductPricesDrawer`'s §8.3 section; `ProductEditPage`'s tile/readiness keep using
 * `governingBaseLine`/`unitAmountOf` directly (WP0.1, unchanged) since they need only the one value.
 */
export function summarizeBase(lines: PriceLineResponse[], now: Date): BaseSummary {
  const governing = governingLine(lines, BASE_SLOT, now);
  const scheduled = scheduledLine(lines, BASE_SLOT, now);
  const pending = pendingLine(lines, BASE_SLOT);
  const negative = negativeLine(lines, BASE_SLOT, governing);
  const otherSlots = groupBySlot(lines.filter((line) => !isBaseSlot(line))).size;
  return { governing, scheduled, pending, negative, otherSlots };
}
